/**
 * AdminVerificationsPage — the project/milestone verification queue.
 *
 * Reads GET /api/admin/verifications (AdminDashboardService.getVerifications),
 * which covers the verification_requests table. Property checks that carry no
 * project appear here too (their ids are PC_-prefixed); contractor vetting
 * lives in its own flow and is not listed.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Skeleton,
  Alert,
  Button,
  Link,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Stack,
  Divider,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';

import adminService from '../services/adminService';
import VerificationsOverview from '../components/admin/VerificationsOverview';
import AssignVerifierButton from '../components/admin/AssignVerifierButton';
import { panelSx, tableSx, tableRowSx } from '../components/admin/surfaces';
import { brand, status as statusTokens, text, bg, border } from '../theme/colors';
import { formatCurrency } from '../utils/helpers';

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

// The two flows have entirely different tables, columns and status
// vocabularies, so each kind carries its own config.
const KINDS = {
  work: {
    label: 'Work',
    title: 'Work verifications',
    noun: 'verification',
    columns: ['Reference', 'Project', 'Client', 'Verifier', 'Status', 'Fee', 'Requested', 'Created'],
    // verification_requests_status_check
    statuses: [
      'pending', 'assigned', 'accepted', 'in_progress',
      'verification_completed', 'approved', 'completed', 'cancelled',
    ],
    // Server-side groups (AdminDashboardService VERIFICATION_STATUS_GROUPS).
    groups: [
      { key: 'open', label: 'Open (any live status)' },
      { key: 'done', label: 'Done (approved or completed)' },
    ],
    searchPlaceholder: 'Search by reference, project, person or location',
  },
  contractor: {
    label: 'Contractor',
    title: 'Contractor verifications',
    noun: 'contractor file',
    columns: ['Contractor', 'Owner', 'Location', 'Verifier', 'Status', 'Recommendation', 'Risk', 'Due', 'Created'],
    // contractor_verification_assignments_status_check
    // Effective status: the assignment's when one exists, else the file's own
    // (contractor_verifications.verification_status).
    statuses: [
      'pending', 'in_review',
      'assigned', 'in_progress', 'submitted', 'changes_requested',
      'approved', 'rejected', 'cancelled',
    ],
    groups: [
      { key: 'open', label: 'Open (incl. awaiting review)' },
      { key: 'done', label: 'Closed (approved or rejected)' },
    ],
    searchPlaceholder: 'Search by contractor, company or verifier',
  },
};

// Whole-row tint for a decided verification: light green approved, light red
// rejected. Everything else is untinted.
const rowTintForStatus = (status) => {
  if (status === 'approved') return statusTokens.successBg;
  if (status === 'rejected') return statusTokens.errorBg;
  return undefined;
};

// Grouped by what an admin cares about: waiting, moving, done, dead.
const STATUS_TONES = {
  pending: statusTokens.warning,
  in_review: statusTokens.info,
  rejected: statusTokens.error,
  assigned: statusTokens.info,
  accepted: statusTokens.info,
  in_progress: statusTokens.info,
  submitted: statusTokens.warning,
  changes_requested: statusTokens.warning,
  verification_completed: statusTokens.success,
  approved: statusTokens.success,
  completed: statusTokens.success,
  cancelled: statusTokens.neutral,
};

const RISK_TONES = {
  low: statusTokens.success,
  standard: statusTokens.info,
  elevated: statusTokens.warning,
  high: statusTokens.error,
};

const EMPTY = '—';

const titleise = (value) => (value || '').replace(/_/g, ' ');

const formatDate = (value) => {
  if (!value) return EMPTY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const AdminVerificationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const parsedPage = parseInt(searchParams.get('page'), 10);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage - 1 : 0;

  const parsedLimit = parseInt(searchParams.get('limit'), 10);
  const rowsPerPage = PAGE_SIZES.includes(parsedLimit) ? parsedLimit : DEFAULT_PAGE_SIZE;

  // No (or unknown) ?kind= means the overview: the two flows are different
  // enough that a merged list would mislead.
  const rawKind = searchParams.get('kind');
  const isOverview = !KINDS[rawKind];
  const kind = isOverview ? 'work' : rawKind;
  const config = KINDS[kind];

  const urlSearch = searchParams.get('search') || '';
  const assignedFilter = ['yes', 'no'].includes(searchParams.get('assigned'))
    ? searchParams.get('assigned')
    : null;
  const rawStatus = searchParams.get('status');
  const groupKeys = (config.groups || []).map((g) => g.key);
  const statusFilter = config.statuses.includes(rawStatus) || groupKeys.includes(rawStatus)
    ? rawStatus
    : 'all';
  const hasFilters = !!urlSearch || statusFilter !== 'all' || !!assignedFilter;

  const writeParams = (updates, { replace = false } = {}) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === 'all' || value == null) params.delete(key);
      else params.set(key, String(value));
    });
    setSearchParams(params, { replace });
  };

  const setPagination = (nextPage, nextLimit = rowsPerPage, { replace = false } = {}) =>
    writeParams(
      {
        page: nextPage > 0 ? nextPage + 1 : '',
        limit: nextLimit !== DEFAULT_PAGE_SIZE ? nextLimit : '',
      },
      { replace }
    );

  const setFilter = (updates, { replace = false } = {}) =>
    writeParams({ ...updates, page: '' }, { replace });

  /**
   * `isCancelled` guards against out-of-order responses: a slower earlier
   * request must not overwrite a newer one, or the table renders rows for a
   * filter the user has already moved off. Supplied by the effect below;
   * user-initiated calls (Retry, refresh) pass nothing and always apply.
   */
  const load = useCallback(async ({ isCancelled = () => false } = {}) => {
    if (isOverview) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { verifications, pagination } = await adminService.getVerifications({
        kind,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(urlSearch ? { search: urlSearch } : {}),
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(assignedFilter ? { assigned: assignedFilter } : {}),
      });
      if (isCancelled()) return;
      setRows(verifications || []);
      setTotal(pagination?.total ?? 0);
      setError(null);
    } catch (err) {
      if (isCancelled()) return;
      setError(err?.response?.data?.error || err?.message || 'Could not load verifications.');
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [isOverview, kind, page, rowsPerPage, urlSearch, statusFilter, assignedFilter]);

  useEffect(() => {
    let cancelled = false;
    load({ isCancelled: () => cancelled });
    return () => { cancelled = true; };
  }, [load]);

  const [searchInput, setSearchInput] = useState(urlSearch);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== urlSearch) {
        setFilter({ search: searchInput.trim() }, { replace: true });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, urlSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearchInput((current) => (current.trim() === urlSearch ? current : urlSearch));
  }, [urlSearch]);

  useEffect(() => {
    setSearchInput(searchParams.get('search') || '');
  }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading || total === 0 || page === 0) return;
    const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
    if (page > lastPage) setPagination(lastPage, rowsPerPage, { replace: true });
  }, [loading, total, page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const openUser = (id) =>
    navigate(`/admin/home/users/${id}`, {
      state: { listSearch: location.search, listPath: location.pathname },
    });

  /**
   * A contractor recorded by name only — no platform account — comes back as
   * { id: null, name }. There is no user record to open, and the detail route
   * validates :id as a UUID, so linking would send /admin/home/users/null and
   * get a 400. Those render as plain text.
   */
  const personCell = (person) => {
    if (!person) return EMPTY;

    if (!person.id) {
      return (
        <Box>
          <Typography component="span" sx={{ fontSize: 'inherit', color: text.secondary }}>
            {person.name || person.email || EMPTY}
          </Typography>
          <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
            No platform account
          </Typography>
        </Box>
      );
    }

    return (
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={() => openUser(person.id)}
        sx={{
          color: text.secondary,
          fontSize: 'inherit',
          fontFamily: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          '&:hover': { color: brand.primary },
        }}
      >
        {person.name || person.email}
      </Link>
    );
  };

  // Early return sits after every hook, so hook order stays stable.
  if (isOverview) return <VerificationsOverview />;

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary, mb: 0.25 }}>
        {config.title}
      </Typography>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 2.5 }}>
        {loading
          ? 'Loading…'
          : `${total.toLocaleString()} ${config.noun}${total === 1 ? '' : 's'}${
              groupKeys.includes(statusFilter)
                ? ` · ${(config.groups.find((g) => g.key === statusFilter) || {}).label}`
                : hasFilters ? ' matching' : ''
            }`}
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <TextField
          size="small"
          placeholder={config.searchPlaceholder}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: { md: 300 }, bgcolor: bg.paper }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: text.tertiary }} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search">
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setFilter({ status: e.target.value })}
          sx={{ minWidth: 210, bgcolor: bg.paper }}
        >
          <MenuItem value="all">All statuses</MenuItem>
          {(config.groups || []).map((g) => (
            <MenuItem key={g.key} value={g.key} sx={{ fontWeight: 600 }}>
              {g.label}
            </MenuItem>
          ))}
          <Divider />
          {config.statuses.map((st) => (
            <MenuItem key={st} value={st} sx={{ textTransform: 'capitalize' }}>
              {titleise(st)}
            </MenuItem>
          ))}
        </TextField>

        <Chip
          label="Unassigned only"
          clickable
          onClick={() => setFilter({ assigned: assignedFilter === 'no' ? '' : 'no' })}
          sx={{
            fontWeight: 600,
            fontSize: 12,
            color: assignedFilter === 'no' ? statusTokens.warningDark : text.secondary,
            bgcolor: assignedFilter === 'no' ? statusTokens.warningBg : 'transparent',
            border: `1px solid ${assignedFilter === 'no' ? statusTokens.warning : border.light}`,
            whiteSpace: 'nowrap',
          }}
        />

        {hasFilters && (
          <Button
            onClick={() => setFilter({ search: '', status: 'all', assigned: '' })}
            sx={{ textTransform: 'none', color: text.secondary, whiteSpace: 'nowrap' }}
          >
            Clear all
          </Button>
        )}
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2.5, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={panelSx}>
        <TableContainer>
          <Table size="medium" sx={tableSx}>
            <TableHead>
              <TableRow>
                {config.columns.map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {config.columns.map((label) => (
                      <TableCell key={label}>
                        <Skeleton height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={config.columns.length} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: text.tertiary }}>
                      {hasFilters
                        ? `No ${config.noun}s match these filters.`
                        : `No ${config.noun}s yet.`}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((v) => {
                  const tone = STATUS_TONES[v.status] || statusTokens.neutral;
                  const statusChip = (
                    <Chip
                      size="small"
                      label={titleise(v.status)}
                      sx={{
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        fontSize: 11,
                        height: 22,
                        color: tone,
                        bgcolor: `${tone}1a`,
                      }}
                    />
                  );

                  if (kind === 'contractor') {
                    const riskTone = RISK_TONES[v.riskLevel] || statusTokens.neutral;
                    return (
                      <TableRow key={v.id} sx={{ ...tableRowSx, bgcolor: rowTintForStatus(v.status) }}>
                        <TableCell sx={{ color: text.secondary }}>
                          {/* Company name opens the submitted verification file
                              (profile + portfolio); the Owner column links to
                              the person's user page. */}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => navigate(
                              `/admin/home/verifications/contractor/${v.verificationId}`,
                              { state: { listSearch: location.search, listPath: location.pathname } }
                            )}
                            sx={{
                              color: text.primary,
                              fontWeight: 600,
                              fontSize: 'inherit',
                              fontFamily: 'inherit',
                              textAlign: 'left',
                              cursor: 'pointer',
                              '&:hover': { color: brand.primary },
                            }}
                          >
                            {v.contractor?.name || EMPTY}
                          </Link>
                          {!v.contractor?.id && (
                            <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                              No platform account
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: text.secondary }}>
                          {personCell(v.owner)}
                        </TableCell>
                        <TableCell sx={{ color: text.secondary, maxWidth: 180 }}>
                          <Typography variant="body2" noWrap title={v.location || ''} sx={{ color: 'inherit' }}>
                            {v.location || EMPTY}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: text.secondary }}>
                          {v.verifier ? personCell(v.verifier) : (
                            <AssignVerifierButton kind="contractor" row={v} onAssigned={load} />
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{statusChip}</TableCell>
                        <TableCell sx={{ color: text.secondary, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                          {titleise(v.recommendation)}
                          {v.confidenceScore != null && (
                            <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                              {v.confidenceScore}% confidence
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {v.riskLevel ? (
                            <Chip
                              size="small"
                              label={titleise(v.riskLevel)}
                              sx={{
                                textTransform: 'capitalize',
                                fontWeight: 600,
                                fontSize: 11,
                                height: 22,
                                color: riskTone,
                                bgcolor: `${riskTone}1a`,
                              }}
                            />
                          ) : (
                            EMPTY
                          )}
                        </TableCell>
                        <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                          {formatDate(v.dueAt)}
                        </TableCell>
                        <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                          {formatDate(v.createdAt)}
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return (
                    <TableRow key={v.id} sx={{ ...tableRowSx, bgcolor: rowTintForStatus(v.status) }}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: text.primary }}>
                          {v.id}
                        </Typography>
                        {(v.locationCity || v.locationAddress) && (
                          <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                            {v.locationCity || v.locationAddress}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ color: text.secondary }}>
                        {v.projectId ? (
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              navigate(`/admin/home/projects/${encodeURIComponent(v.projectId)}`, {
                                state: { listSearch: location.search, listPath: location.pathname },
                              })
                            }
                            sx={{
                              color: text.secondary,
                              fontSize: 'inherit',
                              fontFamily: 'inherit',
                              textAlign: 'left',
                              cursor: 'pointer',
                              '&:hover': { color: brand.primary },
                            }}
                          >
                            {v.projectTitle || v.projectId}
                          </Link>
                        ) : (
                          // Standalone property checks carry no project.
                          <Typography variant="caption" sx={{ color: text.tertiary }}>
                            Standalone
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ color: text.secondary }}>{personCell(v.client)}</TableCell>

                      <TableCell sx={{ color: text.secondary }}>
                        {v.verifier ? (
                          personCell(v.verifier)
                        ) : (
                          <AssignVerifierButton kind="work" row={v} onAssigned={load} />
                        )}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{statusChip}</TableCell>

                      <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                        {v.estimatedFee != null ? formatCurrency(v.estimatedFee) : EMPTY}
                      </TableCell>

                      <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                        {formatDate(v.requestedDate)}
                      </TableCell>

                      <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                        {formatDate(v.createdAt)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => setPagination(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setPagination(0, parseInt(e.target.value, 10))}
          rowsPerPageOptions={PAGE_SIZES}
          sx={{ borderTop: `1px solid ${border.light}` }}
        />
      </Paper>
    </Box>
  );
};

export default AdminVerificationsPage;
